"use client";
import GoogleButton from "@/components/GoogleButton";
import ShareApp from "@/components/ShareApp";
import { ServerContext } from "@/context/Server";
import { useApp, useAppStore, useHandleClickCreateNewSong } from "@/store/app";
import { APP_NAME } from "@/utils/constants";
import useInfo from "@/utils/info";
import type { BeforeInstallPromptEvent } from "@/utils/types";
import Image from "next/image";
import { type ReactNode, useContext, useEffect } from "react";
import shallow from "zustand/shallow";

export const WelcomeSteps = {
  FirstWelcome: 0,
  SignIn: 1,
  AddToHomeScreen: 2,
  AddASong: 3,
  InviteFriends: 4,
};

const LOG_TAG = "Welcome";

let beforeInstallPromptEvent: BeforeInstallPromptEvent;
export default function Welcome() {
  //throw new Error("Welcome test error");
  const { session } = useContext(ServerContext);
  const isAppInstalled = false; // TODO: firebase
  const { welcomeStep, setWelcomeStep, user } = useAppStore(
    ({ welcomeStep, setWelcomeStep, user }) => ({
      welcomeStep,
      setWelcomeStep,
      user,
    }),
    shallow,
  );
  //console.debug(LOG_TAG, { sessionIsNull: session === null, welcomeStep });

  useEffect(() => {
    if (session && welcomeStep === -1) {
      setWelcomeStep(WelcomeSteps.AddASong);
    } else if (welcomeStep === -1) {
      setWelcomeStep(WelcomeSteps.FirstWelcome);
    } else if (user && welcomeStep === 0) {
      setWelcomeStep(WelcomeSteps.AddASong);
    } /* else if (
      (isAppInstalled || !beforeInstallPromptEvent) &&
      welcomeStep === 2
    ) {
      console.debug("Welcome skipping AddToHomescreen", {
        isAppInstalled,
        beforeInstallPromptEvent,
      });
      setWelcomeStep(welcomeStep + 1);
    }*/
  }, [welcomeStep, setWelcomeStep, user, session]);

  const steps = [
    <FirstWelcome key={0} />,
    <SignIn key={1} />,
    <AddToHomeScreen key={2} />,
    <AddASong key={3} />,
    <InviteFriends key={4} />,
  ];

  return (
    <div className="flex h-full justify-center overflow-auto">
      <div className="max-w-[360px] p-4" key={welcomeStep}>
        {steps[welcomeStep]}
      </div>
    </div>
  );
}

const FirstWelcome = () => {
  const { signIn } = useApp();
  return (
    <div className="text-accent-content text-sm tracking-[0.01071em]">
      <div>
        <Image
          priority
          unoptimized
          src="/FirstWelcome.svg"
          width={328}
          height={184}
          alt="Welcome to Songbook"
        />
      </div>
      <p className="my-3.5">
        <b>Your Songbook. (Made with Friends)</b>
      </p>
      <p>
        A simple and clean way to view the songs you play together. Online,
        offline, or printed.
      </p>
      <div className="mt-16 flex justify-center overflow-auto">
        <GoogleButton onClick={signIn} label="Sign in" />
      </div>
    </div>
  );
};

export const SignIn = ({ skipFn }: { skipFn?: () => void }) => {
  const { signIn } = useApp();
  return (
    <div className="flex flex-col items-center px-8">
      <div>
        <Image
          priority
          src="/Stepper-Dropbox-Large.svg"
          width={328}
          height={185}
          alt="Sign in to Songbook"
        />
      </div>
      <p>
        Sign in to backup your chords &amp; lyrics and access them from other
        devices.
      </p>
      <ButtonOrSkip skipFn={skipFn}>
        <GoogleButton onClick={signIn} label="Sign in" />
      </ButtonOrSkip>
    </div>
  );
};

const AddToHomeScreen = () => {
  const { isLayoutPhone } = useInfo();

  const onClickInstall = () => {
    const e = beforeInstallPromptEvent;
    console.debug("AddToHomeScreen showPrompt", { e });
    window.analyticsLogEvent("AddToHomeScreen ShowInstallPrompt");

    // Show the install prompt
    e.prompt();
    // Wait for the user to respond to the prompt
    e.userChoice.then((choiceResult) => {
      window.analyticsLogEvent("AddToHomeScreen Outcome", {
        outcome: choiceResult.outcome,
      });
      if (choiceResult.outcome === "accepted") {
        console.debug("User accepted the install prompt");
      } else {
        console.debug("User dismissed the install prompt");
      }
    });
  };

  return (
    <div>
      <div>
        <Image
          unoptimized
          src={
            isLayoutPhone
              ? "/Stepper-Install-Mobile-Large.svg"
              : "/Stepper-Install-Computer-Large.svg"
          }
          width={328}
          height={185}
          alt="Share your Songbook"
        />
      </div>
      <p>
        {APP_NAME} lets you create and share chords &amp; lyrics with your
        friends.
      </p>
      <p>
        Add a shortcut to your homescreen to make this webapp easier to find and
        use!
      </p>
      <ButtonOrSkip>
        <button className="btn btn-primary" onClick={onClickInstall}>
          Add to Homescreen
        </button>
      </ButtonOrSkip>
    </div>
  );
};

const AskForSongs = () => {
  return (
    <div>
      <div>
        <Image
          unoptimized
          src="/Stepper-Friends-Large.svg"
          width={328}
          height={185}
          alt="Invite friends to Songbook"
        />
      </div>
      <p>
        The fastest way to get a great songbook is to ask your friends to share
        the songs in their songbook with you.
      </p>

      <p>
        As they add more songs you’ll benefit from their work, and vice versa.{" "}
      </p>
      <ButtonOrSkip>
        <ShareApp useButtonText="Ask for songs" />
      </ButtonOrSkip>
    </div>
  );
};

const AddASong = () => {
  const handleClickCreateNewSong = useHandleClickCreateNewSong();

  return (
    <div>
      <div>
        <Image
          priority
          src="/Stepper-AddSong-Large.svg"
          width={328}
          height={185}
          alt="Add a song to Songbook"
        />
      </div>
      <p className="mb-4">
        Add your favorite songs to {APP_NAME} to access them from anywhere.
      </p>
      <p className="mb-4">
        You can share them with friends, view them on any device (even offline),
        download them, print them, and do so much more.
      </p>
      <ButtonOrSkip>
        <button
          className="btn btn-primary text-black rounded"
          onClick={handleClickCreateNewSong}
        >
          Add a song
        </button>
      </ButtonOrSkip>
    </div>
  );
};

const InviteFriends = () => {
  return (
    <div>
      <div>
        <Image
          unoptimized
          src="/Stepper-Friends-Large.svg"
          width={328}
          height={185}
          alt="Invite friends to Songbook"
        />
      </div>
      <p>Who do you like to play music with?</p>
      <p>
        Share your songs with bandmates and fans, or create a songbook with
        friends to enjoy around the campfire.
      </p>
      <ButtonOrSkip>
        <ShareApp useButtonText="Invite your friends" />
      </ButtonOrSkip>
    </div>
  );
};

const P = ({ children }: { children: ReactNode }) => <p>{children}</p>;

const ButtonOrSkip = ({
  children,
  skipFn,
  skipText,
}: {
  children: ReactNode;
  skipFn?: () => void;
  skipText?: string;
}) => {
  const { setWelcomeStep, welcomeStep } = useAppStore(
    ({ setWelcomeStep, welcomeStep }) => ({
      setWelcomeStep,
      welcomeStep,
    }),
    shallow,
  );
  return (
    <div className="mt-8 flex justify-center">
      <div className="mx-2">{children}</div>
      {skipFn && (
        <div className="mx-2">
          <button
            className="btn btn-ghost rounded"
            onClick={() => {
              //console.debug("skip", skipFn);
              if (skipFn) {
                skipFn();
                return;
              }
              setWelcomeStep(welcomeStep + 1);
              window.analyticsLogEvent("Clicked Skip Welcome Step", {
                welcomeStep: welcomeStep,
              });
            }}
          >
            {skipText ? skipText : "Skip"}
          </button>
        </div>
      )}
    </div>
  );
};
